/**
 * Calculate the next version number based on the current version and bump type
 * @param current Current version string (e.g., "1.0", "1.5", "2.3")
 * @param bump Version bump type: "major" or "minor"
 * @returns Next version string
 */
export function calculateNextVersion(
  current: string,
  bump: "major" | "minor",
): string {
  const parts = current.split(".");

  if (parts.length !== 2) {
    throw new Error(`Invalid version format: ${current}. Expected format: X.Y`);
  }

  const major = parseInt(parts[0], 10);
  const minor = parseInt(parts[1], 10);

  if (isNaN(major) || isNaN(minor)) {
    throw new Error(`Invalid version numbers in: ${current}`);
  }

  if (bump === "major") {
    return `${major + 1}.0`;
  } else {
    return `${major}.${minor + 1}`;
  }
}

/**
 * Compare two version strings
 * @returns Negative if v1 < v2, 0 if equal, positive if v1 > v2
 */
export function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split(".").map(Number);
  const parts2 = v2.split(".").map(Number);

  const major1 = parts1[0] || 0;
  const minor1 = parts1[1] || 0;
  const major2 = parts2[0] || 0;
  const minor2 = parts2[1] || 0;

  if (major1 !== major2) {
    return major1 - major2;
  }
  return minor1 - minor2;
}

/**
 * Format a date/time to a human-readable string
 */
export function formatVersionDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString();
}
